import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const UpcomingSessionsPanel = ({ sessions }) => {
  const navigate = useNavigate();

  const formatSessionTime = (timestamp) => {
    const date = new Date(timestamp);
    return date?.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatBookingDateTime = (bookingDate, bookingTime) => {
    if (!bookingDate) return '';
    const dateStr = new Date(bookingDate)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
    if (!bookingTime) return dateStr;
    const [hours, minutes] = bookingTime?.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${dateStr} at ${displayHour}:${minutes || '00'} ${ampm}`;
  };

  const getSessionTypeIcon = (type) => {
    return type === 'one_on_one' ? 'Video' : 'Users';
  };

  const getSessionTypeLabel = (type) => {
    return type === 'one_on_one' ? '1:1 Counseling' : 'Group Presentation';
  };

  // Normalize: supports both counseling_sessions and counselor_bookings shapes
  const normalizeSession = (session) => {
    if (session?.scheduledAt || session?.scheduled_at) {
      return {
        id: session?.id,
        instituteName: session?.institute?.name || 'Institute Session',
        sessionType: session?.sessionType || session?.session_type || 'one_on_one',
        displayTime: formatSessionTime(session?.scheduledAt || session?.scheduled_at),
        durationMinutes: session?.durationMinutes || session?.duration_minutes || 30,
        status: session?.status || 'scheduled',
        meetingUrl: session?.meetingUrl || session?.meeting_url
      };
    }
    // counselor_bookings shape
    return {
      id: session?.id,
      instituteName: session?.institute?.name || 'Institute Session',
      sessionType: 'one_on_one',
      displayTime: formatBookingDateTime(session?.bookingDate || session?.booking_date, session?.bookingTime || session?.booking_time),
      durationMinutes: session?.durationMinutes || session?.duration_minutes || 30,
      status: session?.status || 'confirmed',
      meetingUrl: session?.meetingLink || session?.meeting_link
    };
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
          <Icon name="Calendar" size={20} color="var(--color-accent)" />
        </div>
        <div>
          <h2 className="text-lg font-heading font-semibold text-foreground">
            Upcoming Sessions
          </h2>
          <p className="text-sm text-muted-foreground">
            {sessions?.length || 0} scheduled
          </p>
        </div>
      </div>

      <div className="space-y-4">
        {sessions?.length === 0 ? (
          <div className="text-center py-8">
            <Icon name="Calendar" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
            <p className="text-muted-foreground">No upcoming sessions</p>
            <p className="text-xs text-muted-foreground mt-1">Book a session from an institute's virtual booth</p>
          </div>
        ) : (
          sessions?.map((session) => {
            const s = normalizeSession(session);
            const isConfirmed = s?.status === 'confirmed' || s?.status === 'scheduled';
            return (
              <div
                key={s?.id}
                className="p-4 bg-background rounded-lg border border-border hover:border-primary transition-smooth"
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon
                      name={getSessionTypeIcon(s?.sessionType)}
                      size={20}
                      color="var(--color-primary)"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <h3 className="font-medium text-foreground truncate">
                        {s?.instituteName}
                      </h3>
                      {isConfirmed && (
                        <span className="flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 text-xs font-semibold rounded-full whitespace-nowrap flex-shrink-0">
                          <Icon name="CheckCircle" size={11} />
                          Confirmed
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">
                      {getSessionTypeLabel(s?.sessionType)}
                    </p>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground mb-3">
                      <div className="flex items-center gap-1">
                        <Icon name="Clock" size={14} />
                        <span>{s?.displayTime}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Icon name="Timer" size={14} />
                        <span>{s?.durationMinutes} mins</span>
                      </div>
                    </div>
                    {s?.meetingUrl && (
                      <Button
                        variant="default"
                        size="sm"
                        iconName="Video"
                        iconPosition="left"
                        onClick={() => navigate(s?.meetingUrl?.startsWith('/') ? s?.meetingUrl : `/video-counseling?session=${s?.id}`)}>
                        Join Session
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default UpcomingSessionsPanel;